import { useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Debounced server-side search for outbound/inbound trucks. Returns matches that may live
 * outside the client's buffered 90-day window, so operators can find old trucks the
 * BaseDashboard fetch missed.
 *
 * Behavior:
 *   - Idle (term shorter than minLength) → returns { results: [], status: 'idle' }
 *   - Active term → debounces by `debounceMs`, then GETs /<endpoint>?search=<term>&limit=<limit>
 *   - Cancels in-flight on unmount or when term changes
 *
 * @param {Object}   options
 * @param {string}   options.mode        'outbound' | 'inbound' — picks the API endpoint.
 * @param {string}   options.searchTerm  Current operator-typed query.
 * @param {Object}   [options.extraParams] Additional query params (e.g. { status: 'Completed' }).
 * @param {number}   [options.debounceMs]  Default 250.
 * @param {number}   [options.minLength]   Default 2.
 * @param {number}   [options.limit]       Default 200.
 * @param {boolean}  [options.enabled]     If false, hook stays idle. Default true.
 */
export default function useServerTruckSearch({
  mode,
  searchTerm,
  extraParams = {},
  debounceMs = 250,
  minLength = 2,
  limit = 200,
  enabled = true,
}) {
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'error'
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  // Stable serialisation of extraParams so changes (e.g. status filter flipping) re-fire.
  const extraParamsKey = JSON.stringify(extraParams);

  useEffect(() => {
    if (!enabled || !searchTerm || searchTerm.trim().length < minLength) {
      setResults([]);
      setStatus('idle');
      setError(null);
      return undefined;
    }

    const handle = setTimeout(async () => {
      // Abort any in-flight request from a prior keystroke.
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const endpoint = mode === 'inbound' ? 'inbound-trucks' : 'outbound-trucks';
      const params = new URLSearchParams({ search: searchTerm.trim(), limit: String(limit) });
      Object.entries(extraParams).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
      });

      setStatus('loading');
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/${endpoint}?${params.toString()}`, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Search failed (${res.status})`);
        const json = await res.json();
        setResults(Array.isArray(json.trucks) ? json.trucks : []);
        setStatus('ready');
      } catch (err) {
        if (err.name === 'AbortError') return;
        setError(err.message || 'Search failed');
        setResults([]);
        setStatus('error');
      }
    }, debounceMs);

    return () => {
      clearTimeout(handle);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [enabled, mode, searchTerm, extraParamsKey, debounceMs, minLength, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  return { results, status, error };
}
